import React, { useState, useEffect } from 'react';
import { 
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Download,
  Filter,
  BarChart3,
  PieChart,
  LineChart,
  Calculator,
  Plus,
  Search,
  AlertCircle,
  FileText,
  Euro,
  Activity
} from 'lucide-react';
import { api } from '../utils/api';
import TransactionModal from '../components/TransactionModal';
import { exportToCSV } from '../utils/export';

const FinancialsPage = () => {
  const [timeframe, setTimeframe] = useState('all');
  const [transactions, setTransactions] = useState([]);
  const [events, setEvents] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    loadFinancialData();
  }, [timeframe]);

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate date range based on timeframe
      const params = {};
      const now = new Date();
      
      if (timeframe === 'month') {
        params.startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        params.endDate = now.toISOString();
      } else if (timeframe === 'year') {
        params.startDate = new Date(now.getFullYear(), 0, 1).toISOString();
        params.endDate = now.toISOString();
      }

      // Load all data in parallel
      const [transactionsResponse, eventsResponse, reportResponse] = await Promise.all([
        api.transactions.getAll(params),
        api.events.getAll(),
        api.reports.financial(params)
      ]);

      setTransactions(transactionsResponse.transactions || []);
      setEvents(eventsResponse.events || []);
      setReport(reportResponse.report);
    } catch (error) {
      console.error('Error loading financial data:', error);
      setError('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = async (transactionData) => {
    try {
      const response = await api.transactions.create(transactionData);
      await loadFinancialData(); // Reload to get updated data
    } catch (error) {
      console.error('Error adding transaction:', error);
      throw error;
    }
  };

  const handleExportTransactions = () => {
    const exportData = filteredTransactions.map(txn => ({
      Date: new Date(txn.date).toLocaleDateString(),
      Type: txn.type.charAt(0).toUpperCase() + txn.type.slice(1),
      Category: txn.category,
      Description: txn.description,
      Event: events.find(e => e.id === txn.eventId)?.name || '',
      Amount: `€${txn.amount.toFixed(2)}`,
      Reference: txn.reference || '',
      Vendor: txn.vendor || ''
    }));
    
    exportToCSV(exportData, 'transactions.csv');
  };

  const filteredTransactions = transactions.filter(txn => {
    const matchesSearch = 
      txn.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      txn.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      txn.reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || txn.type === filterType;
    const matchesCategory = filterCategory === 'all' || txn.category === filterCategory;
    return matchesSearch && matchesType && matchesCategory;
  });

  // Get unique categories from transactions
  const categories = [...new Set(transactions.map(t => t.category))].sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A574] mx-auto mb-4"></div>
          <p className="text-[#888888]">Loading financial data...</p>
        </div>
      </div>
    );
  }

  const summary = report?.summary || {
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    profitMargin: 0
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light mb-2">Financials</h1>
          <p className="text-[#888888]">Track revenue, expenses, and profitability</p>
        </div>
        <div className="flex gap-3">
          <select 
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            className="input-dark"
          >
            <option value="all">All Time</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
          <button 
            onClick={() => setShowTransactionModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            Add Transaction
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} className="text-red-500" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card-dark">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="text-green-500" size={24} />
            <TrendingUp className="text-green-500" size={20} />
          </div>
          <p className="text-2xl font-light mb-1">€{summary.totalRevenue.toLocaleString()}</p>
          <p className="text-sm text-[#888888]">Total Revenue</p>
        </div>
        <div className="card-dark">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="text-red-500" size={24} />
            <TrendingDown className="text-red-500" size={20} />
          </div>
          <p className="text-2xl font-light mb-1">€{summary.totalExpenses.toLocaleString()}</p>
          <p className="text-sm text-[#888888]">Total Expenses</p>
        </div>
        <div className="card-dark">
          <div className="flex items-center justify-between mb-2">
            <Calculator className={summary.netProfit >= 0 ? "text-green-500" : "text-red-500"} size={24} />
            <span className={`text-xs ${summary.netProfit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {summary.profitMargin}%
            </span>
          </div>
          <p className={`text-2xl font-light mb-1 ${summary.netProfit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            €{Math.abs(summary.netProfit).toLocaleString()}
          </p>
          <p className="text-sm text-[#888888]">Net {summary.netProfit >= 0 ? 'Profit' : 'Loss'}</p>
        </div>
        <div className="card-dark">
          <div className="flex items-center justify-between mb-2">
            <Activity className="text-[#D4A574]" size={24} />
            <span className="text-xs text-[#888888]">{report?.transactionCount || 0}</span>
          </div>
          <p className="text-2xl font-light mb-1">{filteredTransactions.length}</p>
          <p className="text-sm text-[#888888]">Transactions</p>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Category */}
        {report?.revenueByCategory && Object.keys(report.revenueByCategory).length > 0 && (
          <div className="card-dark">
            <h2 className="text-xl font-light mb-4">Revenue by Category</h2>
            <div className="space-y-4">
              {Object.entries(report.revenueByCategory)
                .sort(([,a], [,b]) => b - a)
                .map(([category, amount]) => {
                  const percentage = summary.totalRevenue > 0 
                    ? (amount / summary.totalRevenue * 100).toFixed(1)
                    : 0;
                  return (
                    <div key={category}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm">{category}</span>
                        <span className="text-sm font-medium">€{amount.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-[#0A0A0A] rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Expenses by Category */}
        {report?.expensesByCategory && Object.keys(report.expensesByCategory).length > 0 && (
          <div className="card-dark">
            <h2 className="text-xl font-light mb-4">Expenses by Category</h2>
            <div className="space-y-4">
              {Object.entries(report.expensesByCategory)
                .sort(([,a], [,b]) => b - a)
                .map(([category, amount]) => {
                  const percentage = summary.totalExpenses > 0 
                    ? (amount / summary.totalExpenses * 100).toFixed(1)
                    : 0;
                  return (
                    <div key={category}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm">{category}</span>
                        <span className="text-sm font-medium">€{amount.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-[#0A0A0A] rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Event Profitability */}
      {report?.eventProfitability && report.eventProfitability.length > 0 && (
        <div className="card-dark">
          <h2 className="text-xl font-light mb-4">Event Profitability</h2>
          <div className="overflow-x-auto">
            <table className="table-dark">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Date</th>
                  <th className="text-right">Revenue</th>
                  <th className="text-right">Expenses</th>
                  <th className="text-right">Profit</th>
                  <th className="text-right">Margin</th>
                </tr>
              </thead>
              <tbody>
                {report.eventProfitability
                  .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
                  .map(event => (
                    <tr key={event.eventId}>
                      <td>{event.eventName}</td>
                      <td>{event.date ? new Date(event.date).toLocaleDateString() : '-'}</td>
                      <td className="text-right">€{event.revenue.toLocaleString()}</td>
                      <td className="text-right">€{event.expenses.toLocaleString()}</td>
                      <td className={`text-right font-medium ${
                        event.profit >= 0 ? 'text-green-500' : 'text-red-500'
                      }`}>
                        €{Math.abs(event.profit).toLocaleString()}
                      </td>
                      <td className="text-right">{event.margin}%</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transaction Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 text-[#888888]" size={20} />
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-dark w-full pl-10"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="input-dark"
        >
          <option value="all">All Types</option>
          <option value="revenue">Revenue</option>
          <option value="expense">Expenses</option>
        </select>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="input-dark"
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <button 
          onClick={handleExportTransactions}
          className="btn-secondary flex items-center gap-2"
        >
          <Download size={20} />
          Export
        </button>
      </div>

      {/* Transactions Table */}
      <div className="card-dark">
        <h2 className="text-xl font-light mb-4">Recent Transactions</h2>
        <div className="overflow-x-auto">
          <table className="table-dark">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Category</th>
                <th>Description</th>
                <th>Event</th>
                <th className="text-right">Amount</th>
                <th>Reference</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.slice(0, 50).map((txn) => (
                <tr key={txn.id} className="hover:bg-[#0A0A0A] cursor-pointer">
                  <td>{new Date(txn.date).toLocaleDateString()}</td>
                  <td>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      txn.type === 'revenue' 
                        ? 'bg-green-500/20 text-green-500' 
                        : 'bg-red-500/20 text-red-500'
                    }`}>
                      {txn.type}
                    </span>
                  </td>
                  <td>{txn.category}</td>
                  <td>
                    <div>
                      <p>{txn.description}</p>
                      {txn.vendor && (
                        <p className="text-xs text-[#888888]">{txn.vendor}</p>
                      )}
                    </div>
                  </td>
                  <td className="text-sm">
                    {txn.eventId ? events.find(e => e.id === txn.eventId)?.name || '-' : '-'}
                  </td>
                  <td className={`text-right font-medium ${
                    txn.type === 'revenue' ? 'text-green-500' : 'text-red-500'
                  }`}>
                    €{txn.amount.toFixed(2)}
                  </td>
                  <td className="text-sm text-[#888888]">{txn.reference || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredTransactions.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto text-[#333333] mb-4" size={48} />
            <p className="text-[#888888]">No transactions found</p>
          </div>
        )}
      </div>

      {/* Add/Edit Transaction Modal */}
      <TransactionModal
        isOpen={showTransactionModal || !!editingTransaction}
        onClose={() => {
          setShowTransactionModal(false);
          setEditingTransaction(null);
        }}
        transaction={editingTransaction}
        events={events}
        onSave={handleAddTransaction}
      />
    </div>
  );
};

export default FinancialsPage;